import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import {
  SolicitudService,
  SolicitudItem,
  SolicitudesQueryParams,
  construirSolicitudSchemaApiDesdeItemPlano,
} from '../../services/solicitud.service';
import { ToastService } from '../../services/toast.service';
import { AuthStore } from '../../../store/auth.store';
import { Router } from '@angular/router';
import type { ProductorPlanNegocioItem } from '../../services/negocio.service';
import { calcularIdJerarquiaParaEndpoint } from '../../utils/perfil.util';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/** Altura aproximada de una fila del thead (px). */
const ALTURA_HEADER_TABLA = 44;
/** Altura aproximada de una fila del tbody (px). */
const ALTURA_FILA = 44;
/** Altura aproximada del bloque de paginación (px). */
const ALTURA_PAGINACION = 60;
/** Altura aproximada del header de la tarjeta (título, buscador, padding) (px). */
const ALTURA_HEADER_TARJETA = 130;
/** Mínimo de filas a mostrar por página. */
const TAMANIO_PAGINA_MINIMO = 5;

@Component({
  selector: 'app-tarjeta-solicitudes-pendientes',
  templateUrl: './tarjeta-solicitudes-pendientes.component.html',
  styleUrls: ['./tarjeta-solicitudes-pendientes.component.scss'],
  standalone: false,
})
export class TarjetaSolicitudesPendientesComponent implements OnInit, AfterViewInit, OnDestroy {
  private static contadorId = 0;
  readonly idUnico: string;

  @ViewChild('tarjetaCard') tarjetaCardRef?: ElementRef<HTMLElement>;

  private readonly solicitudService = inject(SolicitudService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly authStore = inject(AuthStore);

  private resizeObserver: ResizeObserver | null = null;
  private contextSub: Subscription | null = null;

  /** Título principal de la tarjeta */
  @Input() titulo = 'Solicitudes Pendientes';

  /** Subtítulo descriptivo */
  @Input() subtitulo = 'Solicitudes por revisar';

  /** Texto del popover de ayuda */
  @Input() informacion =
    'Solicitudes de aprobación y reapertura de planes de trabajo que requieren su atención.';

  /** Tamaño de página por defecto si no se puede medir el contenedor */
  @Input() tamanioPaginaInicial = 10;

  /** Items de la página actual (desde la API) */
  solicitudes = signal<SolicitudItem[]>([]);
  cargando = signal<boolean>(false);

  /** Paginación (server-side). tamanioPagina se calcula según el alto del contenedor. */
  paginaActual = 1;
  tamanioPagina = 10;
  totalPaginas = 1;
  totalRegistros = 0;

  /** Término de búsqueda (enviado al API) */
  terminoBusqueda = '';

  /** Ordenación server-side (según OpenAPI: sort, order) */
  sortBy = 'nombre_productor';
  sortOrder: 'asc' | 'desc' = 'asc';

  /** Columnas ordenables; sortKey es el campo enviado al API en el param `sort`. */
  readonly columnasOrdenables: { id: string; label: string; sortKey: string }[] = [
    { id: 'usuario', label: 'Usuario', sortKey: 'nombre_productor' },
    { id: 'tipo', label: 'Tipo', sortKey: 'tipo_solicitud' }
  ];

  constructor() {
    this.idUnico = `informacion-solicitudes-${TarjetaSolicitudesPendientesComponent.contadorId++}`;
  }

  ngOnInit(): void {
    this.tamanioPagina = this.tamanioPaginaInicial;
    this.contextSub = combineLatest([
      this.authStore.perfilActivo$,
      this.authStore.idRegionSeleccionada$,
      this.authStore.idAreaSeleccionada$,
      this.authStore.unidadOperativaActiva$,
    ])
      .pipe(
        map(([perfil, region, area, uo]) => ({
          idPerfil: perfil?.id_perfil ?? null,
          idJerarquia: calcularIdJerarquiaParaEndpoint(perfil, region, area, uo),
        })),
        distinctUntilChanged(
          (a, b) => a.idPerfil === b.idPerfil && a.idJerarquia === b.idJerarquia
        )
      )
      .subscribe(() => this.cargarSolicitudes());
  }

  ngAfterViewInit(): void {
    const card = this.tarjetaCardRef?.nativeElement;
    if (!card) return;
    const altoDisponible = card.clientHeight - ALTURA_HEADER_TARJETA - ALTURA_PAGINACION;
    if (altoDisponible > 0) this.actualizarTamanioPaginaSegunAlto(altoDisponible);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height - ALTURA_HEADER_TARJETA - ALTURA_PAGINACION;
        if (h > 0) this.actualizarTamanioPaginaSegunAlto(h);
      }
    });
    this.resizeObserver.observe(card);
  }

  ngOnDestroy(): void {
    this.contextSub?.unsubscribe();
    this.contextSub = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  nombreUsuarioCorto(item: SolicitudItem): string {
    const nombre = (item.productor_nombre ?? '').trim();
    const primerApellido = (item.productor_primer_apellido ?? '').trim();
    const partes = [nombre, primerApellido].filter(Boolean);
    if (partes.length) return partes.join(' ');

    // Fallback defensivo si el backend no trae productor desnormalizado.
    const tokens = (item.nombre_productor ?? '—').trim().split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) return `${tokens[0]} ${tokens[1]}`;
    return tokens[0] ?? '—';
  }

  /**
   * Calcula cuántas filas caben en el área de la tabla (alto ya descontando header de tarjeta y paginación)
   * y actualiza tamanioPagina; si cambió, recarga la página actual.
   */
  private actualizarTamanioPaginaSegunAlto(altoAreaTabla: number): void {
    const altoParaFilas = altoAreaTabla - ALTURA_HEADER_TABLA;
    const filasQueCaben = Math.floor(altoParaFilas / ALTURA_FILA);
    const nuevoTamanio = Math.max(TAMANIO_PAGINA_MINIMO, filasQueCaben);
    if (nuevoTamanio === this.tamanioPagina) return;
    this.tamanioPagina = nuevoTamanio;
    this.paginaActual = 1;
    this.cargarSolicitudes();
  }

  cargarSolicitudes(): void {
    const idPerfil = this.authStore.obtenerPerfilActivo()?.id_perfil;
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();
    if (idPerfil == null) {
      this.solicitudes.set([]);
      this.totalRegistros = 0;
      this.totalPaginas = 1;
      this.cargando.set(false);
      return;
    }

    this.cargando.set(true);
    const params: SolicitudesQueryParams = {
      page: this.paginaActual,
      size: this.tamanioPagina,
      sort: this.sortBy,
      order: this.sortOrder,
    };
    if (this.terminoBusqueda.trim()) {
      params.search = this.terminoBusqueda.trim();
    }
    this.solicitudService.obtenerSolicitudesPendientes(idPerfil, idJerarquia, params).subscribe({
      next: (respuesta) => {
        this.solicitudes.set(respuesta.items || []);
        this.totalRegistros = respuesta.total ?? 0;
        this.totalPaginas = respuesta.pages ?? Math.max(1, Math.ceil(this.totalRegistros / this.tamanioPagina));
        this.paginaActual = respuesta.page ?? 1;
        this.tamanioPagina = respuesta.size ?? this.tamanioPagina;
        this.cargando.set(false);
      },
      error: () => {
        this.solicitudes.set([]);
        this.totalRegistros = 0;
        this.totalPaginas = 1;
        this.cargando.set(false);
      },
    });
  }

  /** Busca por el término ingresado (recarga con página 1) */
  buscarSolicitudes(): void {
    this.paginaActual = 1;
    this.cargarSolicitudes();
  }

  /** Limpia el término de búsqueda */
  limpiarBusqueda(): void {
    this.terminoBusqueda = '';
    this.paginaActual = 1;
    this.cargarSolicitudes();
  }

  paginaAnterior(): void {
    if (this.paginaActual > 1) {
      this.paginaActual--;
      this.cargarSolicitudes();
    }
  }

  paginaSiguiente(): void {
    if (this.paginaActual < this.totalPaginas) {
      this.paginaActual++;
      this.cargarSolicitudes();
    }
  }

  irPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaActual = pagina;
      this.cargarSolicitudes();
    }
  }

  /** Cambia la ordenación al hacer clic en una columna. */
  onSortColumn(columnaId: string): void {
    const col = this.columnasOrdenables.find(c => c.id === columnaId);
    if (!col) return;
    if (this.sortBy === col.sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col.sortKey;
      this.sortOrder = 'asc';
    }
    this.paginaActual = 1;
    this.cargarSolicitudes();
  }

  /** Indica si la columna con el sortKey dado es la que está ordenando actualmente. */
  isColumnaOrdenActiva(sortKey: string): boolean {
    return this.sortBy === sortKey;
  }

  /** Array de números de página visibles (para los botones de paginación) */
  getPaginasArray(): number[] {
    const paginas: number[] = [];
    const maxPaginasVisibles = 5;
    let inicio = Math.max(1, this.paginaActual - Math.floor(maxPaginasVisibles / 2));
    let fin = Math.min(this.totalPaginas, inicio + maxPaginasVisibles - 1);
    if (fin - inicio < maxPaginasVisibles - 1) {
      inicio = Math.max(1, fin - maxPaginasVisibles + 1);
    }
    for (let i = inicio; i <= fin; i++) {
      paginas.push(i);
    }
    return paginas;
  }

  /**
   * Arma un ítem mínimo para `GestionAccionesStateService` desde la solicitud (OpenAPI ya trae productor y plan).
   * Evita mostrar el encabezado vacío si el productor no está en la primera página del listado de negocio.
   */
  private productorDesdeSolicitud(s: SolicitudItem): ProductorPlanNegocioItem {
    const idProductor = s.id_productor!.trim();
    const idPlan = s.id_plan_negocio?.trim();
    let nombre = (s.productor_nombre ?? '').trim();
    let primerApellido = (s.productor_primer_apellido ?? '').trim() || null;
    if (!nombre && !primerApellido) {
      const partes = (s.nombre_productor ?? '—').trim().split(/\s+/).filter(Boolean);
      nombre = partes[0] ?? '—';
      primerApellido = partes.length > 1 ? partes.slice(1).join(' ') : null;
    }
    const apellidos = primerApellido;
    const ultimaSolicitud =
      s.solicitudSchemaApi ?? construirSolicitudSchemaApiDesdeItemPlano(s);
    return {
      id_productor: idProductor,
      id_jerarquia_productor: this.authStore.obtenerIdJerarquiaParaEndpoint(),
      rut: null,
      nombre,
      primer_apellido: primerApellido,
      segundo_apellido: null,
      apellidos,
      unidad_operativa: null,
      area: null,
      comuna: null,
      email: null,
      ultima_visita: null,
      coordenadas: null,
      id_estado_solicitud: s.id_estado_solicitud ?? null,
      ultima_solicitud: ultimaSolicitud,
      plan_negocio: idPlan
        ? {
            id_plan_negocio: idPlan,
            estado: s.estado_plan_descripcion ?? null,
            id_estado_plan_negocio: s.id_estado_plan_negocio ?? null,
            id_estado_solicitud: s.id_estado_solicitud ?? null,
          }
        : null,
    };
  }

  /**
   * Misma navegación que el submenú lateral "Diagnóstico" en Gestión (/gestion/acciones/:id/diagnostico).
   * Pasa `state.productor` para alinear estado con la URL al venir de otra ficha (p. ej. otro id en sesión).
   */
  async irADiagnosticoProductor(solicitud: SolicitudItem): Promise<void> {
    const idProductor = solicitud.id_productor?.trim();
    if (!idProductor) {
      await this.toastService.mostrar(
        'No se puede abrir el diagnóstico: esta solicitud no incluye el identificador del productor.',
        'warning'
      );
      return;
    }
    const idPlan = solicitud.id_plan_negocio?.trim();
    if (!idPlan) {
      await this.toastService.mostrar(
        'La solicitud no trae plan de negocio asociado. Se abrirá el diagnóstico del productor; si hay errores al cargar, revise los datos en Gestión.',
        'warning'
      );
    }
    await this.router.navigate(['/gestion', 'acciones', idProductor, 'diagnostico'], {
      state: {
        productor: this.productorDesdeSolicitud(solicitud),
        /** Encabezado mínimo (sin RUT/unidad): hidratar con GET negocio en destino. */
        encabezadoProductorPendiente: true,
      },
    });
  }
}
